// The Studio singleton tab's centre: the project the author is in (Studio v1
// D1; `frame.studioProject`, `/projects/:id`) in three columns (pen "Studio",
// Decided 29 Oct; #315): the outline, the material one page at a time (and
// the pages about it, Info and Sources: StudioAbout.tsx), and the action
// panel (StudioRight.tsx). The old canvas shows instead only at a
// hand-typed `/classic/...` (`frame.studioSurface`, ClassicStudioCentre.tsx);
// both read the same project state.
//
// Projects the project's cards from the Library the shell already holds and
// maps the pages' intents onto the shell's existing doors:
//  - the page shown is the desk's path, kept per device (studioDesk.ts);
//  - a part row moves a part or takes it out, each with an Undo
//    (useStudioCommands.ts); a module in a course duplicates;
//  - briefs (decisions 2 and 3): a course page plans a module, a module page
//    a document with its brief, a planned document's brief is changed and
//    generated from;
//  - what waits for the author (the project's export check,
//    useProjectIssues.ts, PS-54) marks the outline and the page links, and
//    is the action panel's Needs you, with what stands before exporting
//    what Export sends out (useDeskReadiness.ts) and "Generate it";
//  - "N sources" in the page's facts line opens the Sources page;
//  - the top bar's breadcrumb says where the page shown sits (pen Q76); the
//    bar undoes or redoes in the document shown (the editor's history; the
//    project has none yet) and opens History;
//  - Help and feedback sits at the outline's foot (pen Q76), never on the
//    page;
//  - Export opens the export sheet for the whole content (the project's
//    course), or for the course or module shown when there is no one course;
//  - with no project open, or the open one gone (archived, deleted, or a
//    link to one that is not the author's: studioOpen.ts), the empty view:
//    Go to Projects and New project (pen "Scen · Studio, nothing open").

import * as Limits from "@bilda/core/limits";
import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { ProjectIssues } from "../../../app/ports/project.js";
import { Button } from "../../../components/Button/Button.js";
import { AskLine } from "../../../components/Editor/AskLine/AskLine.js";
import { EmptyState } from "../../../components/EmptyState/EmptyState.js";
import { Icon } from "../../../components/Icon/Icon.js";
import {
    AnchoredPopover,
    Popover,
    type PopoverProps,
} from "../../../components/Popover/Popover.js";
import { projectStudioCards } from "../../../components/Studio/studioCards.js";
import {
    type DeskCommand,
    openAboutCommands,
    openThingCommands,
} from "../../../components/Studio/studioDesk.js";
import { beforeExportItems } from "../../../components/StudioDesk/DeskBriefs.js";
import {
    type DocState,
    docStateOf,
    toGenerate,
} from "../../../components/StudioDesk/deskFacts.js";
import { BeingMadeLine } from "../../../components/StudioEditor/BeingMadeLine.js";
import { useAboutLabel } from "../../../components/StudioEditor/Outline.js";
import { StudioCreateMenu } from "../../../components/StudioEditor/StudioCreateMenu.js";
import { StudioEditor } from "../../../components/StudioEditor/StudioEditor.js";
import { StudioTopBar } from "../../../components/StudioEditor/StudioTopBar.js";
import {
    type Landing,
    landingChoices,
    landingOf,
    partsBefore,
    shownStep,
    sourceRowsOf,
} from "../../../components/StudioEditor/studioCreate.js";
import {
    outlineRows,
    rootOf,
    studioPage,
    topCrumbsOf,
    type Waiting,
} from "../../../components/StudioEditor/studioPages.js";
import { LandingMarksProvider } from "../../../components/StudioLanding/landingMarks.js";
import type { ActionView } from "../../../components/StudioPanel/StudioPanel.js";
import { beingMadeOf } from "../../../modules/chat/beingMade.js";
import { HelpEntry } from "../chrome/FeedbackHost.js";
import { useAppShell } from "../context.js";
import type { CreateAt } from "../useStudioCreate.js";
import { ClassicStudioCentre } from "./ClassicStudioCentre.js";
import { EditedDocument } from "./MaterialCentre.js";
import { StudioAbout } from "./StudioAbout.js";
import { StudioDocument } from "./StudioDocument.js";
import {
    landingPageOf,
    StudioLandingPanel,
    studioLandingMarks,
} from "./StudioLanding.js";
import { StudioRight } from "./StudioRight.js";
import { runPartIntent } from "./studioIntents.js";
import { studioOpenOf } from "./studioOpen.js";
import { useDeskReadiness } from "./useDeskReadiness.js";
import { landingFormatOf, useHowItLands } from "./useHowItLands.js";
import { useProjectIssues } from "./useProjectIssues.js";
import { useProjectObjectives } from "./useProjectObjectives.js";

// Suggestions waiting, by resource id: proposals open on a material, an AI
// draft not reviewed yet (one each).
const waitingOf = (issues: ProjectIssues | undefined): Waiting =>
    new Map(
        issues?.type === "project_has_issues"
            ? issues.issues.flatMap((issue): readonly [string, number][] => {
                  switch (issue.type) {
                      case "issue_proposals_open":
                          return [[issue.material.id, issue.count]];
                      case "issue_draft_unreviewed":
                          return [[issue.part.id, 1]];
                      default:
                          return [];
                  }
              })
            : [],
    );

// Whether a document shows in its editor (StudioDocument.tsx): everything
// but one not written yet or being written.
const writtenDoc = (state: DocState): boolean => {
    switch (state) {
        case "planned":
        case "writing":
            return false;
        case "generated":
        case "empty":
        case "removed":
        case "source":
        case "unknown":
            return true;
        default:
            return exhaustSwitchCase(state);
    }
};

export const StudioCentre = (): ReactNode => {
    const { library, frame } = useAppShell();
    const open = studioOpenOf(
        frame.studioProject,
        library === null ? null : (library.projects ?? []),
    );
    switch (open.type) {
        case "none":
            return <NothingOpen gone={false} />;
        case "gone":
            return <NothingOpen gone />;
        case "loading":
        case "open":
            return frame.studioSurface === "classic" ? (
                <ClassicStudioCentre />
            ) : (
                <StudioPages />
            );
        default:
            return exhaustSwitchCase(open);
    }
};

// The Studio with no project open: the way to all projects, or a new one.
// With none at all yet, New project leads. `gone`: the open project is no
// longer there (the same line whatever the reason, SF-22).
const NothingOpen = ({ gone }: { readonly gone: boolean }): ReactNode => {
    const { t } = useLingui();
    const { library, dispatch } = useAppShell();
    const first = library !== null && (library.projects ?? []).length === 0;
    const goToProjects = (
        <Button
            key="projects"
            variant={first ? "secondary" : "primary"}
            onClick={() => dispatch({ type: "open-projects" })}
        >
            {t`Go to Projects`}
        </Button>
    );
    const newProject = (
        <Button
            key="new"
            variant={first ? "primary" : "secondary"}
            onClick={() =>
                dispatch({
                    type: "open-overlay",
                    overlay: { type: "new-project" },
                })
            }
        >
            {t`New project`}
        </Button>
    );
    return (
        <div className="bilda-studio-empty">
            <EmptyState
                icon={<Icon name="square-pen" />}
                title={t`No project open`}
                body={
                    gone ? (
                        <>
                            {t`This project is no longer available.`}
                            <br />
                            {t`Choose a project and it opens here.`}
                        </>
                    ) : (
                        t`Choose a project and it opens here.`
                    )
                }
            >
                {first
                    ? [newProject, goToProjects]
                    : [goToProjects, newProject]}
            </EmptyState>
        </div>
    );
};

const StudioPages = (): ReactNode => {
    const { t } = useLingui();
    const {
        library,
        searchResults,
        frame,
        dispatch,
        health,
        studio,
        openOverlay,
        ports,
        editor,
        showNotice,
        activeMaterialId,
        sessionFor,
        activeConversationId,
        openFilePicker,
    } = useAppShell();
    const issues = useProjectIssues(ports, frame.studioProject, library);
    const objectives = useProjectObjectives(
        ports,
        frame.studioProject,
        library,
    );
    // The action panel's view: view state, Chat first.
    const [view, setView] = useState<ActionView>("chat");
    const online = health === "online";
    const project = (library?.projects ?? []).find(
        (p) => p.id === frame.studioProject,
    );
    const projectTitle = project?.title ?? "";
    const canvas = new Set((project?.canvas ?? []).map((i) => i.target));
    const cards = projectStudioCards(searchResults, library, canvas).filter(
        (c) => canvas.has(c.id),
    );
    const waiting = waitingOf(issues);
    const root = rootOf(cards);
    const page = studioPage(cards, studio.desk, projectTitle, waiting);
    const top = topCrumbsOf(page, projectTitle, cards, studio.desk);
    const aboutLabel = useAboutLabel();
    // What Export sends out: the whole content when it is one course, else
    // the course or module shown.
    const exportTarget =
        root.type === "course"
            ? root.card.id
            : page.type === "container"
              ? page.id
              : undefined;
    const readiness = useDeskReadiness(
        ports,
        studio.desk,
        library,
        exportTarget,
    );
    // How what Export sends out lands in the format the project is made
    // for: Export opens it in place of the action panel (no such format:
    // the export sheet, as before).
    const landingFormat = landingFormatOf(project?.format);
    const landingPage = landingPageOf(page);
    const landing = useHowItLands(
        ports,
        library,
        exportTarget,
        landingFormat,
        cards,
        studio.onDesk,
        landingPage?.id,
        () => showNotice({ type: "edit-failed" }),
    );
    const openExport =
        exportTarget === undefined
            ? undefined
            : () =>
                  openOverlay({
                      type: "export",
                      materialId: exportTarget,
                  });
    const targetCard =
        root.type === "course"
            ? root.card
            : page.type === "container"
              ? page.container
              : undefined;
    const targetReadiness =
        targetCard === undefined ? undefined : readiness.get(targetCard.id);
    const beforeExport =
        targetCard === undefined || targetReadiness === undefined
            ? undefined
            : {
                  where: targetCard.title,
                  items: beforeExportItems(targetReadiness),
                  planned: toGenerate(targetReadiness, targetCard),
                  note: studio.notes.get(targetCard.id),
                  onGenerateAll: (ids: readonly string[]) =>
                      studio.generateAll(targetCard.id, ids),
              };
    // Undo and Redo of the page being written on (the editor's own history;
    // the project has none yet): a written document, a course or module page
    // (its own text, pen "Always writing"). A page about the project, and the
    // project's own page with no single course, have none.
    const writtenOn = ((): boolean => {
        switch (page.type) {
            case "document":
                return (
                    page.doc.kind === "material" &&
                    writtenDoc(docStateOf(page.doc))
                );
            case "container":
                return true;
            case "root":
                return page.container !== undefined;
            case "about":
                return false;
            default:
                return exhaustSwitchCase(page);
        }
    })();
    const history = writtenOn
        ? {
              canUndo: editor.canUndo,
              canRedo: editor.canRedo,
              onUndo: editor.undo,
              onRedo: editor.redo,
          }
        : undefined;
    const create = studio.create;
    // The container whose page is shown (null: not a course or module page;
    // undefined: the project's own page with no course).
    const shownContainer =
        page.type === "container"
            ? page.id
            : page.type === "root"
              ? page.container?.id
              : null;
    // The way to a container's page (absent: the project's own page).
    const toPageOf = (container: string | undefined): readonly DeskCommand[] =>
        container !== undefined
            ? openThingCommands(cards, container)
            : studio.desk.path
                  .slice(0, 1)
                  .map((e): DeskCommand => ({ type: "close", id: e.id }));
    // While the assistant works on a request: "Being made: <words>" where it
    // lands (principle 2), until its first part lands or the turn ends. On a
    // course or module page among its lines; above a document; at the
    // outline's foot on a page about the project.
    const making = beingMadeOf(sessionFor(activeConversationId));
    const beingMade = (className: string) =>
        making === undefined ? null : (
            <BeingMadeLine
                words={making.words}
                outline={making.outline}
                className={className}
            />
        );
    // The line that asks on a page, where the part would land (pen Q66).
    const asking = create.asking;
    const ask =
        asking?.type === "in_page"
            ? {
                  container: asking.container,
                  at: asking.index,
                  line: (
                      <AskLine
                          words={asking.words}
                          online={online}
                          max={Limits.LONG_TEXT_MAX}
                          className="bilda-pages__ask"
                          onAsk={create.askSend}
                          // A line from a button, not text on the page: Esc
                          // lets it go; a click elsewhere keeps written
                          // words in it.
                          onLeave={(words, how) =>
                              how === "blur" && words.trim() !== ""
                                  ? undefined
                                  : create.askLeave()
                          }
                      />
                  ),
              }
            : making !== undefined && shownContainer !== null
              ? {
                    container: shownContainer,
                    at: undefined,
                    line: beingMade("bilda-pages__ask"),
                }
              : undefined;
    // The one Create menu, opened at `at`; a part made in `landing` goes to
    // `index` there (absent: its end; a place picked with Change: its end).
    const createMenu = (
        at: CreateAt,
        landing: Landing | undefined,
        index: number | undefined,
    ): Omit<PopoverProps, "trigger"> => {
        const placed = (container: string | undefined) =>
            container === landing?.id ? index : undefined;
        // The project's sources as step 1 lists them (pen Q73), most
        // recently touched first.
        const sources = sourceRowsOf(
            cards,
            new Map(
                (library?.sources ?? []).flatMap(
                    (s): readonly (readonly [string, string])[] => {
                        const when = s.lastEditedAt ?? s.createdAt;
                        return when === undefined ? [] : [[s.id, when]];
                    },
                ),
            ),
        );
        const asked = create.ask?.at === at ? create.ask.step : null;
        const step =
            asked === null
                ? null
                : shownStep(
                      asked,
                      sources.map((s) => s.id),
                  );
        const caret = at.startsWith("caret:")
            ? at.slice("caret:".length)
            : undefined;
        return {
            open: step !== null,
            onOpenChange: (open: boolean) =>
                open
                    ? create.open({ at, step: { type: "start" } })
                    : create.close(),
            // Escape goes back one step, then closes (pen Q73).
            onEscape: () => {
                if (step !== null) create.back(step);
            },
            label: t`Create`,
            side: "bottom",
            align: "start",
            look: "bare",
            children:
                step === null ? null : (
                    <StudioCreateMenu
                        step={step}
                        opening={caret === undefined ? "button" : "caret"}
                        landing={landing}
                        choices={landingChoices(cards)}
                        sources={sources}
                        online={online}
                        intents={{
                            onStep: (next) => create.open({ at, step: next }),
                            onBack: () => create.back(step),
                            onAskHere: (words, container, source) => {
                                // For one source at the caret: the line "/"
                                // was typed in asks (pen Q73).
                                if (caret !== undefined && source !== undefined)
                                    return create.askInLine(caret, source);
                                create.askHere(
                                    words,
                                    container,
                                    placed(container),
                                    source,
                                );
                                // The line stands on the page it lands on.
                                if (container !== shownContainer)
                                    studio.onDesk(...toPageOf(container));
                            },
                            onInsert: (kind, container) =>
                                create.insert(
                                    kind,
                                    container,
                                    placed(container),
                                ),
                            onFromSource: (source, kind, container) =>
                                create.fromSource(
                                    source,
                                    kind,
                                    container,
                                    placed(container),
                                ),
                            // The ordinary add flow; the menu waits on step
                            // 1 and picks the source once it lands.
                            onAddSource: () => {
                                create.open({
                                    at,
                                    step: {
                                        type: "adding",
                                        known: sources.map((s) => s.id),
                                    },
                                });
                                openFilePicker();
                            },
                        }}
                    />
                ),
        };
    };
    // The + in the writing line's margin: the Create menu, landing where the
    // page's parts go (at the end).
    const writingAdd = (
        <Popover
            {...createMenu("page", landingOf(cards, studio.desk), undefined)}
            trigger={
                <button
                    type="button"
                    className="bilda-writing-line__add"
                    aria-label={t`Create`}
                >
                    <Icon name="plus" size="sm" />
                </button>
            }
        />
    );
    const briefs = {
        readiness,
        notes: studio.notes,
        onGenerateAll: studio.generateAll,
    };
    return (
        <LandingMarksProvider
            value={studioLandingMarks(landing, landingPage, cards)}
        >
            <StudioEditor
                cards={cards}
                desk={studio.desk}
                projectTitle={projectTitle}
                online={online}
                waiting={waiting}
                topBar={
                    <StudioTopBar
                        crumbs={top.crumbs}
                        here={
                            top.here.type === "title"
                                ? top.here.title
                                : aboutLabel(top.here.id)[1]
                        }
                        menuShown={frame.nav === "expanded"}
                        online={online}
                        onOpen={(commands) => studio.onDesk(...commands)}
                        onToggleMenu={() => dispatch({ type: "toggle-rail" })}
                        history={history}
                        onHistory={() => openOverlay({ type: "history" })}
                        onExport={
                            landingFormat === undefined ||
                            openExport === undefined
                                ? openExport
                                : landing.onOpen
                        }
                    />
                }
                help={<HelpEntry look="row" />}
                sources={
                    library === null
                        ? undefined
                        : cards.filter((c) => c.type === "source").length
                }
                onSources={() => studio.onDesk(...openAboutCommands("sources"))}
                right={
                    landing.open &&
                    landingFormat !== undefined &&
                    openExport !== undefined ? (
                        <StudioLandingPanel
                            panel={landing}
                            format={landingFormat}
                            page={landingPage}
                            cards={cards}
                            online={online}
                            onExport={openExport}
                        />
                    ) : (
                        <StudioRight
                            view={view}
                            onView={setView}
                            cards={cards}
                            issues={issues}
                            beforeExport={beforeExport}
                            whole={
                                root.type === "course"
                                    ? t`Whole course`
                                    : t`Whole project`
                            }
                            proposals={
                                landing.answer?.type === "landed"
                                    ? landing.answer.landing.proposals
                                    : []
                            }
                            moving={landing.moving}
                            onShowMe={landing.onShowMe}
                            onMoveIt={landing.onMoveIt}
                        />
                    )
                }
                onDesk={studio.onDesk}
                onCaret={(at) => studio.noteCaret(at.blockId, at.selection)}
                onPart={runPartIntent(studio)}
                onMoveTo={(from, part, to) =>
                    studio.movePartTo(from.id, part, to)
                }
                onDuplicate={studio.duplicateModule}
                onRename={(id, title, previous) =>
                    studio.rename(id, title, previous, () =>
                        showNotice({ type: "edit-failed" }),
                    )
                }
                renderDocument={(doc) => (
                    <>
                        {beingMade("bilda-pages__being-made")}
                        {/* "/" at the caret opens Ask here or Create from a
                            source… at the block it was typed in (the editor
                            holds the anchor). */}
                        <AnchoredPopover
                            {...createMenu(
                                `caret:${create.caret ?? ""}`,
                                landingOf(cards, studio.desk),
                                undefined,
                            )}
                            around={
                                <StudioDocument doc={doc} add={writingAdd} />
                            }
                        />
                    </>
                )}
                renderContainer={(id, part) =>
                    // The page's own text, once the editor holds it (else its
                    // parts show as a list); a part made from "/" lands after
                    // the parts above the caret.
                    activeMaterialId !== id ||
                    editor.elements === null ||
                    editor.materialError !== null ? null : (
                        <AnchoredPopover
                            {...createMenu(
                                `caret:${create.caret ?? ""}`,
                                landingOf(cards, studio.desk),
                                create.caret === undefined
                                    ? undefined
                                    : partsBefore(
                                          editor.elements,
                                          create.caret,
                                      ),
                            )}
                            around={
                                <EditedDocument
                                    target={undefined}
                                    blankFace="document"
                                    renderPart={(p) => part(p.target)}
                                    add={writingAdd}
                                />
                            }
                        />
                    )
                }
                renderAbout={(about) => (
                    <StudioAbout
                        about={about}
                        cards={cards}
                        outline={outlineRows(
                            cards,
                            studio.desk,
                            projectTitle,
                            new Map(),
                            waiting,
                        )}
                        objectives={objectives}
                    />
                )}
                uncovered={(objectives?.objectives ?? []).some(
                    (o) =>
                        o.taught.length +
                            o.checked.length +
                            o.planned.length ===
                        0,
                )}
                renderCreate={(at) => (
                    <>
                        {at === "outline" && page.type === "about"
                            ? beingMade("bilda-pages__being-made")
                            : null}
                        <Popover
                            {...createMenu(
                                at,
                                landingOf(cards, studio.desk),
                                undefined,
                            )}
                            trigger={
                                at === "outline" ? (
                                    <button
                                        type="button"
                                        className="bilda-pages__create"
                                    >
                                        <Icon name="plus" size="xs" />
                                        {t`Create`}
                                        <Icon name="chevron-down" size="xs" />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="bilda-pages__first"
                                    >
                                        {t`Write, or type / to add a module, page or quiz`}
                                    </button>
                                )
                            }
                        />
                    </>
                )}
                renderAdd={(link) =>
                    link.holder === undefined ? null : (
                        <Popover
                            {...createMenu(
                                `line:${link.id}`,
                                {
                                    id: link.holder.container.id,
                                    title: link.holder.container.title,
                                    depth: 0,
                                    after: link.title,
                                },
                                link.holder.at + 1,
                            )}
                            trigger={
                                <button
                                    type="button"
                                    className="bilda-line__control bilda-line__add"
                                    aria-label={t`Add a line under ${link.title}`}
                                >
                                    <Icon name="plus" size="sm" />
                                </button>
                            }
                        />
                    )
                }
                briefs={briefs}
                ask={ask}
            />
        </LandingMarksProvider>
    );
};
